/**
 * Batching queue for analytics events.
 *
 * Drawing analytics tracks tool activations, which fire orders of magnitude
 * more often than the handful of events this app used to send. One POST per
 * event is not viable at that rate, so events accumulate here and leave in
 * batches.
 *
 * Durability rests on one rule: **an event stays in localStorage until the
 * server has acknowledged it**. Nothing is removed on send, only on success.
 * A tab closed mid-flight, a dropped connection or a crash therefore costs
 * nothing — the queue rehydrates from localStorage on next launch and resends.
 * That is also why the `pagehide` flush can be best-effort without losing data.
 *
 * Offline is the normal case here, not the edge case: inspectors work on site
 * for hours with no network. The buffer is sized for that (see MAX_BUFFERED),
 * and the backlog is drained in chunks the API will accept.
 */

import $ls, { LocalStorageKey } from "@/utils/local-storage";
import APIClient, { APIError } from "@/APIClient";
import { Logger } from "@/logger";
import type { EventContext, EventType } from "@/types/event-logs.types";

/** Send as soon as this many events are waiting. */
const FLUSH_THRESHOLD = 25;
/** Otherwise, send this long after the first event of a batch. */
const FLUSH_DELAY_MS = 10_000;
/**
 * Capped exponential backoff after a failed send, mirroring the oplog's
 * (Syncer.ts): 10s, 20s, 40s… up to 5 min. A backend in trouble must not be
 * hammered every 10s by every client at once.
 */
const FLUSH_BACKOFF_MAX_MS = 5 * 60_000;
/**
 * Events per request. Matches `maxItems` on the server schema — a backlog
 * larger than this is drained in successive chunks, not rejected.
 *
 * Deliberately larger than the oplog's `PUSH_CHUNK_SIZE = 25` (Syncer.ts). That
 * one is kept small so its per-event isolation fallback stays fast, because the
 * oplog carries user content and no event may be lost. Analytics has no such
 * fallback and tolerates loss, so the trade goes the other way: fewer, larger
 * requests to drain a long offline backlog quickly.
 */
export const MAX_BATCH = 200;
/**
 * Hard cap on the buffer, in events. Deliberately NOT tied to MAX_BATCH: how
 * much fits in one request has nothing to do with how much we can hold.
 *
 * Sized from the offline scenario. At ~103 bytes per serialised event, 5000
 * events is ~500 KB against a ~5 MB localStorage quota — storage is not the
 * constraint. 5000 covers a full day at a realistic 10 events/min, or ~4 hours
 * of very heavy use. Past that the oldest are shed, which is logged rather than
 * silent so the under-reporting is at least visible in Sentry.
 */
export const MAX_BUFFERED = 5000;
/**
 * Writing the buffer means serialising all of it, so it cannot happen on every
 * click once the backlog is large. Coalesce writes instead; the exposure is at
 * most this long of activity lost to a hard crash, and the hide/unload paths
 * persist synchronously anyway.
 */
const PERSIST_DEBOUNCE_MS = 1_000;

type PendingEvent = {
  type: EventType;
  date: Date;
  context?: EventContext;
};

let pending: PendingEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let persistTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight = false;
let consecutiveFailures = 0;

function writeBuffer(): void {
  $ls.setItem(LocalStorageKey.EVENT_LOGS, JSON.stringify(pending));
}

/** Write the buffer immediately, cancelling any coalesced write. */
export function persistEventQueue(): void {
  if (persistTimer !== null) {
    clearTimeout(persistTimer);
    persistTimer = null;
  }
  try {
    writeBuffer();
  } catch (error) {
    // Out of quota. Shed the oldest half and try once more: persisting a
    // truncated buffer beats silently persisting nothing and losing everything
    // on the next reload.
    const shed = Math.ceil(pending.length / 2);
    pending.splice(0, shed);
    Logger.warn(
      `event-queue: localStorage full, shed ${shed} oldest event(s) (${error})`,
    );
    try {
      writeBuffer();
    } catch (retryError) {
      Logger.warn(`event-queue: buffer still not persistable (${retryError})`);
    }
  }
}

function persistSoon(): void {
  if (persistTimer !== null) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    persistEventQueue();
  }, PERSIST_DEBOUNCE_MS);
}

/** Recover events left over from a previous session. */
function hydrate(): void {
  try {
    const stored = $ls.getArrayItems(LocalStorageKey.EVENT_LOGS);
    pending = stored
      .filter((event: any) => typeof event?.type === "number")
      .map((event: any) => ({
        type: event.type as EventType,
        date: new Date(event.date),
        context: event.context,
      }));
  } catch (error) {
    Logger.warn(
      `event-queue: could not read buffer, starting empty (${error})`,
    );
    pending = [];
  }
}

hydrate();

function cancelScheduledFlush(): void {
  if (flushTimer === null) return;
  clearTimeout(flushTimer);
  flushTimer = null;
}

function scheduleFlush(): void {
  if (flushTimer !== null) return;
  const delay =
    consecutiveFailures === 0
      ? FLUSH_DELAY_MS
      : Math.min(
          FLUSH_DELAY_MS * 2 ** (consecutiveFailures - 1),
          FLUSH_BACKOFF_MAX_MS,
        );
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushEventQueue();
  }, delay);
}

/**
 * Whether the server refused the payload in a way that retrying cannot fix.
 *
 * 401 is treated as temporary on purpose: the token expired, the user will log
 * back in, and the events should survive that. 408 and 429 are explicit
 * invitations to try again.
 */
function isPermanentRejection(error: unknown): boolean {
  const status = error instanceof APIError ? error.status : undefined;
  if (status === undefined) return false;
  if (status === 401 || status === 408 || status === 429) return false;
  return status >= 400 && status < 500;
}

/**
 * Send ONE chunk of buffered events. Safe to call at any time: concurrent
 * calls, an empty queue and offline devices are all no-ops.
 *
 * Deliberately one chunk per call rather than draining the whole backlog in a
 * loop. Analytics is the least important data this app moves, and nothing waits
 * on it — but a long offline session ends with everyone reconnecting at once,
 * and a client that fired its entire backlog back to back would burst on the
 * backend and compete with the oplog push and drawing saves, which do carry
 * user content. Pacing a 5000-event backlog over a few minutes costs nothing
 * and keeps the connection free for what matters.
 */
export async function flushEventQueue(): Promise<void> {
  if (inFlight || pending.length === 0 || !navigator.onLine) return;

  inFlight = true;
  cancelScheduledFlush();
  const batch = pending.slice(0, MAX_BATCH);

  try {
    await APIClient.createEventLogs(batch);
    // Acknowledged — only now do these leave the buffer.
    pending.splice(0, batch.length);
    consecutiveFailures = 0;
    persistEventQueue();
  } catch (error) {
    if (isPermanentRejection(error)) {
      // The server will never accept this chunk, so retrying it would block the
      // queue forever and pile every later event behind a payload that cannot
      // go through. Drop it and carry on with the rest.
      pending.splice(0, batch.length);
      consecutiveFailures = 0;
      persistEventQueue();
      Logger.warn(
        `event-queue: server rejected ${batch.length} event(s), dropped (${error})`,
      );
    } else {
      // Network failure or server error — keep the backlog and back off.
      consecutiveFailures++;
      Logger.warn(
        `event-queue: flush failed, ${pending.length} event(s) kept (${error})`,
      );
    }
  } finally {
    inFlight = false;
    if (pending.length > 0) scheduleFlush();
  }
}

/** Queue one event. Never throws, never blocks the caller. */
export function enqueueEvent(
  type: EventType,
  context?: EventContext,
  date?: Date,
): void {
  pending.push({
    type,
    date: date ?? new Date(),
    ...(context ? { context } : {}),
  });

  if (pending.length > MAX_BUFFERED) {
    const dropped = pending.length - MAX_BUFFERED;
    pending.splice(0, dropped);
    Logger.warn(`event-queue: buffer full, dropped ${dropped} oldest event(s)`);
  }

  persistSoon();

  // Send straight away on the normal online path, where the queue is just
  // smoothing out bursts of clicks. While catching up on a backlog, stay on the
  // timer instead — otherwise every new click would fire another chunk and undo
  // the pacing that keeps the reconnect burst in check.
  const isCatchingUp = pending.length > MAX_BATCH;
  if (!isCatchingUp && pending.length >= FLUSH_THRESHOLD)
    void flushEventQueue();
  else scheduleFlush();
}

/** Number of events waiting to be acknowledged. Exposed for tests. */
export function pendingEventCount(): number {
  return pending.length;
}
